import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, []);

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const success = useCallback((message, duration) => addToast(message, 'success', duration), [addToast]);
  const error = useCallback((message, duration) => addToast(message, 'error', duration), [addToast]);
  const warning = useCallback((message, duration) => addToast(message, 'warning', duration), [addToast]);
  const info = useCallback((message, duration) => addToast(message, 'info', duration), [addToast]);

  const contextValue = useMemo(() => ({ success, error, warning, info }), [success, error, warning, info]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-20 right-6 z-[10000] space-y-3 pointer-events-none">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

const normalizeMessage = (msg) => {
  if (typeof msg === 'string') return msg;
  if (Array.isArray(msg)) return msg.map(m => (typeof m === 'string' ? m : m?.msg || String(m))).join('; ');
  if (msg?.msg) return msg.msg;
  return String(msg);
};

const shortenMessage = (msg) => {
  const text = normalizeMessage(msg);
  const map = {
    'Balances recalculated successfully': 'Balances recalculated',
    'Failed to load accounts. Please refresh or check your connection.': 'Failed to load accounts',
    'Failed to recalculate balances': 'Failed to recalculate',
    'Payment recorded successfully': 'Payment recorded',
    'Payable created successfully': 'Payable created',
    'Account deleted successfully': 'Account deleted',
    'Failed to create expense': 'Failed to create expense',
    'Failed to update expense': 'Failed to update expense',
    'Failed to delete expense': 'Failed to delete expense',
    'Failed to create budget': 'Failed to create budget',
    'Failed to update budget': 'Failed to update budget',
    'Failed to delete budget': 'Failed to delete budget',
    'Failed to record payment': 'Failed to record payment',
    'Failed to create payable': 'Failed to create payable',
    'Failed to delete account': 'Failed to delete account',
    'Failed to load account ledger': 'Failed to load ledger',
  };
  return map[text] || text;
};

const Toast = ({ toast, onClose }) => {
  const { type, message } = toast;
  const displayMessage = shortenMessage(message);
  const [isExiting, setIsExiting] = React.useState(false);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const icons = {
    success: <CheckCircleIcon className="h-4 w-4 text-teal-600 dark:text-teal-400" />,
    error: <XCircleIcon className="h-4 w-4 text-rose-600 dark:text-rose-400" />,
    warning: <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />,
    info: <InformationCircleIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />,
  };

  const styles = {
    success: 'bg-white dark:bg-gray-900 border-gray-200/80 dark:border-gray-800',
    error: 'bg-white dark:bg-gray-900 border-gray-200/80 dark:border-gray-800',
    warning: 'bg-white dark:bg-gray-900 border-gray-200/80 dark:border-gray-800',
    info: 'bg-white dark:bg-gray-900 border-gray-200/80 dark:border-gray-800',
  };

  const iconBgColors = {
    success: 'bg-teal-50 dark:bg-teal-900/30',
    error: 'bg-rose-50 dark:bg-rose-900/30',
    warning: 'bg-amber-50 dark:bg-amber-900/30',
    info: 'bg-blue-50 dark:bg-blue-900/30',
  };

  return (
    <div
      className={`toast-enter ${isExiting ? 'toast-exit' : ''} ${styles[type]} flex items-center gap-2.5 px-3 py-2.5 rounded-xl border shadow-lg pointer-events-auto max-w-xs`}
      style={{
        boxShadow: '0 8px 32px -4px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)'
      }}
    >
      <div className={`flex-shrink-0 w-8 h-8 rounded-lg ${iconBgColors[type]} flex items-center justify-center`}>
        {icons[type]}
      </div>
      <p className="flex-1 text-xs font-semibold leading-tight text-gray-900 dark:text-white">
        {displayMessage}
      </p>
      <button
        onClick={handleClose}
        className="flex-shrink-0 text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors p-0.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
        aria-label="Close notification"
      >
        <XMarkIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export default Toast;
